/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { observer } from 'mobx-react';
import React, { Component } from 'react';
import Button from '@react/react-spectrum/Button';
import uuid from 'uuid';
import ProductCTA from './productPrimaryCTA';
import './layout.scss';

const shouldShowProdCTA = (urlData, allAppsModel) => {
    if (urlData && urlData.showProductStatusCTA && urlData.sapcode) {
        return allAppsModel.filterApps({ sapcode: urlData.sapcode }).length;
    }
    return false;
};

const renderDefaultCTA = ({
    textList,
    analytics,
    textColor,
    urlList,
    bannerSectionId,
    bannerPlatform,
    name,
    ingestDescription,
    appConfig,
    appsSkeletonModel,
    onAction,
}) => {
    const style = { color: textColor };
    const { bannerAnalyticsCallback } = analytics;
    return (textList[2] && (
        <Button
            label={textList[2]}
            role="button"
            aria-label={textList[2]}
            variant="primary"
            className="action"
            style={{ ...style, borderColor: textColor }}
            onClick={() => {
                const guid = uuid.v4();
                onAction(urlList[0], guid, bannerSectionId, bannerPlatform);
                bannerAnalyticsCallback('bannerCTAClicked',
                    {
                        analytics: { ...analytics, guid, url: urlList[0].url },
                        CTAText: textList[2],
                        ingestId: urlList[0].ingestId,
                        name,
                        ingestDescription,
                    }, appConfig, appsSkeletonModel);
            }} />
    ));
};

@observer
class DefaultItemBannerLayout extends Component {
    renderGuid = uuid.v4();

    componentDidMount() {
        const {
            bannerItem: {
                textList,
                urlList,
                analytics,
                name,
                ingestDescription,
            },
            appConfig,
            appsSkeletonModel,
        } = this.props;

        analytics.bannerAnalyticsCallback('bannerRendered', {
            analytics,
            CTAText: textList[2],
            ingestId: urlList && urlList[0] && urlList[0].ingestId,
            renderGuid: this.renderGuid,
            name,
            ingestDescription,
        }, appConfig, appsSkeletonModel);
    }

    renderLayout = ({
        imageList,
        textList,
        urlList,
        textColor,
        onAction,
        analytics,
        bannerSectionId,
        bannerPlatform,
        name,
        ingestDescription,
        appConfig,
        appsSkeletonModel,
        allAppsModel,
    }) => {
        const contentArray = imageList.map(image => (
            <div className="catalog-banner-image" style={{ backgroundImage: `url('${image}')` }} />
        ));
        const style = { color: textColor };
        if (textList && textList.length > 0) {
            const ele = (
                <div className="layout-content">
                    <div className="title" style={style}>{textList[0]}</div>
                    <div className="desc" style={style}>{textList[1]}</div>
                    {allAppsModel && (shouldShowProdCTA(urlList[0], allAppsModel))
                        ? (
                            <ProductCTA
                                allAppsModel={allAppsModel}
                                urlData={urlList[0]}
                                analytics={analytics}
                                name={name}
                                appsSkeletonModel={appsSkeletonModel}
                                appConfig={appConfig}
                                textColor={textColor} />
                        )
                        : renderDefaultCTA({
                            textList,
                            analytics,
                            textColor,
                            urlList,
                            bannerSectionId,
                            bannerPlatform,
                            name,
                            ingestDescription,
                            appConfig,
                            onAction,
                            appsSkeletonModel,
                        })}

                </div>
            );
            contentArray.push(ele);
        }
        return contentArray;
    };

    render() {
        const {
            bannerItem: {
                imageList,
                textList,
                urlList,
                backgroundColor,
                textColor,
                onAction,
                analytics,
                name,
                ingestDescription,
            },
            bannerSectionId,
            bannerPlatform,
            appConfig,
            appsSkeletonModel,
            allAppsModel,
        } = this.props;

        return (
            <div
                className="default-banner-item-layout"
                style={{ backgroundColor }}
                render-guid={this.renderGuid}
            >
                {this.renderLayout({
                    imageList,
                    textList,
                    urlList,
                    textColor,
                    onAction,
                    analytics,
                    bannerSectionId,
                    bannerPlatform,
                    name,
                    ingestDescription,
                    appConfig,
                    appsSkeletonModel,
                    allAppsModel,
                })}
            </div>
        );
    }
}

export default DefaultItemBannerLayout;
